import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export const LANGUAGE_OPTIONS = Object.freeze([
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "pt", label: "Portuguese" },
]);

export default function LanguageDropdown({ value, onChange }) {
  const languages = useMemo(() => [...LANGUAGE_OPTIONS], []);

  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const selectedLanguage = useMemo(() => {
    const normalized = String(value || "").trim().toLowerCase();
    return languages.find((l) => l.code === normalized) || languages[0];
  }, [languages, value]);

  function updateMenuPosition() {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + window.scrollY + 8,
      left: rect.right + window.scrollX - 208, // aligns right (menu width ~208px)
      width: rect.width,
    });
  }

  useEffect(() => {
    if (!isOpen) return;

    updateMenuPosition();

    function handleResizeOrScroll() {
      updateMenuPosition();
    }

    window.addEventListener("resize", handleResizeOrScroll);
    window.addEventListener("scroll", handleResizeOrScroll, true);

    return () => {
      window.removeEventListener("resize", handleResizeOrScroll);
      window.removeEventListener("scroll", handleResizeOrScroll, true);
    };
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (!isOpen) return;

      const btn = buttonRef.current;
      const menu = menuRef.current;

      if (!btn || !menu) return;

      // close if click is not inside button or menu
      if (!btn.contains(e.target) && !menu.contains(e.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  function setLanguage(code) {
    onChange(code);
    setIsOpen(false);
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="flex cursor-pointer select-none items-center gap-2 rounded border border-slate-300 bg-white/70 px-4 py-2 text-slate-600 shadow-sm hover:bg-white"
      >
        <span className="inline-flex h-5 w-5 items-center justify-center rounded border border-slate-300 text-[11px]">
          A⇄
        </span>

        <span className="text-base">{selectedLanguage.label}</span>

        <svg
          className="ml-1 h-4 w-4 text-slate-500"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[999999] w-52 overflow-hidden rounded border border-slate-200 bg-white shadow-lg"
            style={{
              top: `${menuPos.top}px`,
              left: `${menuPos.left}px`,
            }}
          >
            <div className="max-h-60 overflow-y-auto">
              {languages.map((lang) => {
                const isSelected = lang.code === selectedLanguage.code;

                return (
                  <button
                    key={lang.code}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLanguage(lang.code);
                    }}
                    className={[
                      "flex w-full items-center justify-between px-4 py-2 text-left text-sm",
                      isSelected
                        ? "bg-slate-100 text-slate-900"
                        : "text-slate-700 hover:bg-slate-50",
                    ].join(" ")}
                  >
                    <span>{lang.label}</span>

                    {isSelected && (
                      <svg
                        className="h-4 w-4 text-slate-600"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.704 5.29a1 1 0 010 1.414l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 011.414-1.414l2.793 2.793 6.793-6.793a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
